import { zipStore } from '@/lib/zipStore'
import { byPriority, type MinutesHeader } from './minutesExport'
import type { MinuteItem, Priority } from './types'

/** XML'e güvenli metin: özel karakterler kaçırılır, XML'de yasak kontrol karakterleri atılır. */
const esc = (s: string) =>
  s
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const PRIORITY_COLOR: Record<Priority, string> = { high: 'C0362C', medium: 'B54708', low: '667085' }
const FONT = '<w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri"/>'

type RunOpts = { bold?: boolean; size?: number; color?: string; strike?: boolean }
const run = (text: string, o: RunOpts = {}) =>
  `<w:r><w:rPr>${FONT}${o.bold ? '<w:b/>' : ''}${o.strike ? '<w:strike/>' : ''}${o.color ? `<w:color w:val="${o.color}"/>` : ''}<w:sz w:val="${o.size ?? 22}"/></w:rPr><w:t xml:space="preserve">${esc(text)}</w:t></w:r>`
const para = (runs: string, after = 120) => `<w:p><w:pPr><w:spacing w:before="0" w:after="${after}"/></w:pPr>${runs}</w:p>`

/** Sütun genişlikleri (twip): A4, 2 cm kenar boşluğu → ~9638. */
const COLS = [600, 5738, 1650, 1650]

const cell = (content: string, i: number, fill?: string) =>
  `<w:tc><w:tcPr><w:tcW w:w="${COLS[i]}" w:type="dxa"/>${fill ? `<w:shd w:val="clear" w:color="auto" w:fill="${fill}"/>` : ''}<w:vAlign w:val="center"/></w:tcPr>${para(content, 0)}</w:tc>`

function documentXml(items: MinuteItem[], h: MinutesHeader) {
  const border = (side: string) => `<w:${side} w:val="single" w:sz="4" w:space="0" w:color="D0D5DD"/>`
  const head = `<w:tr><w:trPr><w:tblHeader/></w:trPr>${[h.columns.no, h.columns.note, h.columns.priority, h.columns.status].map((c, i) => cell(run(c, { bold: true, size: 20 }), i, 'F2F4F7')).join('')}</w:tr>`
  const rows = byPriority(items)
    .map((it, n) => {
      const muted = it.done ? '98A2B3' : undefined
      return `<w:tr><w:trPr><w:cantSplit/></w:trPr>${[
        cell(run(String(n + 1), { size: 20, color: '667085' }), 0),
        cell(run(it.text, { strike: it.done, color: muted }), 1),
        cell(run(h.priorityLabel(it.priority), { bold: it.priority === 'high', size: 20, color: PRIORITY_COLOR[it.priority] }), 2),
        cell(run(`${it.done ? '☑' : '☐'} ${it.done ? h.doneLabel : h.openLabel}`, { size: 20, color: it.done ? '067647' : '344054' }), 3),
      ].join('')}</w:tr>`
    })
    .join('')
  const done = items.filter((i) => i.done).length

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${para(run(h.title, { bold: true, size: 36 }), 60)}${para(run(h.customer, { bold: true, size: 24 }), 40)}${para(run(`${h.date} ${h.time} · ${h.staff}`, { size: 20, color: '667085' }), 60)}${para(run(h.summary(done, items.length), { size: 20, color: '667085' }), 240)}<w:tbl><w:tblPr><w:tblW w:w="${COLS.reduce((a, b) => a + b, 0)}" w:type="dxa"/><w:tblBorders>${['top', 'left', 'bottom', 'right', 'insideH', 'insideV'].map(border).join('')}</w:tblBorders><w:tblLayout w:type="fixed"/><w:tblCellMar><w:top w:w="80" w:type="dxa"/><w:left w:w="110" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="110" w:type="dxa"/></w:tblCellMar></w:tblPr><w:tblGrid>${COLS.map((w) => `<w:gridCol w:w="${w}"/>`).join('')}</w:tblGrid>${head}${rows}</w:tbl>${para('', 0)}<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134" w:header="708" w:footer="708" w:gutter="0"/></w:sectPr></w:body></w:document>`
}

/** Word (.docx) belgesi: başlık, müşteri/tarih bilgisi ve öncelik sıralı not tablosu. */
export function toDocx(items: MinuteItem[], h: MinutesHeader): Uint8Array<ArrayBuffer> {
  return zipStore([
    {
      name: '[Content_Types].xml',
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>',
    },
    {
      name: '_rels/.rels',
      data: '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>',
    },
    { name: 'word/document.xml', data: documentXml(items, h) },
  ])
}
